import { Injectable, Signal, signal } from '@angular/core';
import { CRMApiService, FilterGroupOperator, FilterOperator, ListCrmObjectsRequest } from '@vendasta/crm';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs';
import { ContactInfo, ContactInfoInterface } from './contact-info-class';

const PAGE_SIZE = 20;

export const CRM_EXTERNAL_IDS = {
  firstName: 'standard__first_name',
  lastName: 'standard__last_name',
  phoneNumber: 'standard__phone_number',
  email: 'standard__email',
};

@Injectable({
  providedIn: 'root',
})
export class InboxAccountContactService {
  private readonly searchFormFieldValue$$ = new BehaviorSubject<string>('');
  private readonly nextCursorContacts$$ = new BehaviorSubject<string>('');
  private readonly loadMoreContacts$$ = new BehaviorSubject<boolean>(false);
  private readonly loadMoreContactsTrigger$ = this.loadMoreContacts$$.asObservable().pipe(map(() => null));
  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  public readonly loading$ = this.loading$$.asObservable();
  private readonly hasMoreContacts$$ = new BehaviorSubject<boolean>(false);
  public readonly hasMoreContacts$ = this.hasMoreContacts$$.asObservable();
  private readonly contacts$$ = new BehaviorSubject<ContactInfo[]>([]);
  public readonly contacts$ = this.contacts$$.asObservable();
  private _loadingFirstPage = signal(true);
  public loadingFirstPage: Signal<boolean> = this._loadingFirstPage.asReadonly();
  private previousSearchTermContacts = '';
  public partnerId!: string;

  constructor(private crmService: CRMApiService) {}

  public initContacts(): void {
    combineLatest([this.searchFormFieldValue$$, this.loadMoreContactsTrigger$])
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        shareReplay(1),
        switchMap(([searchTerm]) => {
          this.loading$$.next(true);
          let cursor = this.nextCursorContacts$$.value;
          if (this.previousSearchTermContacts !== searchTerm) {
            cursor = '';
            this._loadingFirstPage.set(true);
            this.contacts$$.next([]);
          }
          this.previousSearchTermContacts = searchTerm;
          const lookupRequest = {
            namespace: this.partnerId,
            pagingOptions: {
              cursor: cursor,
              pageSize: PAGE_SIZE,
            },
            search: { searchTerm: searchTerm },
            filtersV2: {
              operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_OR,
              filters: [
                {
                  operator: FilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
                  fieldId: CRM_EXTERNAL_IDS.phoneNumber,
                },
              ],
            },
          } as ListCrmObjectsRequest;
          return this.crmService.listContacts(lookupRequest);
        }),
      )
      .subscribe({
        next: (response) => {
          const crmObjects = response.crmObjects;
          const contacts: ContactInfo[] = [];

          if (!crmObjects) {
            this.hasMoreContacts$$.next(false);
            this.loading$$.next(false);
            this._loadingFirstPage.set(false);
            return;
          }

          crmObjects.forEach((crmObject) => {
            const contactInfo: ContactInfoInterface = { contactId: crmObject.crmObjectId };
            for (const field of crmObject.fields) {
              switch (field.externalId) {
                case CRM_EXTERNAL_IDS.firstName:
                  contactInfo.firstName = field.stringValue;
                  continue;
                case CRM_EXTERNAL_IDS.lastName:
                  contactInfo.lastName = field.stringValue;
                  continue;
                case CRM_EXTERNAL_IDS.email:
                  contactInfo.email = field.stringValue;
                  continue;
                case CRM_EXTERNAL_IDS.phoneNumber:
                  contactInfo.phone = field.stringValue;
                  continue;
              }
            }
            contacts.push(new ContactInfo(contactInfo));
          });

          this.contacts$$.next(this.contacts$$.value.concat(contacts));
          this.hasMoreContacts$$.next(response.pagingMetadata.hasMore);
          this.nextCursorContacts$$.next(response.pagingMetadata.nextCursor);
          this.loading$$.next(false);
          this._loadingFirstPage.set(false);
        },
        error: () => {
          this.loading$$.next(false);
          this._loadingFirstPage.set(false);
        },
      });
  }

  set searchFormFieldValue(value: string) {
    this.searchFormFieldValue$$.next(value);
  }

  set loadMoreContacts(value: boolean) {
    this.loadMoreContacts$$.next(value);
  }
}
